'use client';

import { useState, useEffect } from 'react';
import { Clock, Globe, ArrowRight } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { timeZoneConverterContent } from '../../data/time-zone-converter-content';

const timeZones = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)', offset: 0 },
  { value: 'GMT', label: 'GMT (Greenwich Mean Time)', offset: 0 },

  // US Eastern
  { value: 'EST', label: 'EST (Eastern Standard Time)', offset: -5 },
  { value: 'EDT', label: 'EDT (Eastern Daylight Time)', offset: -4 },

  // US Central
  { value: 'CST_US', label: 'CST (Central Standard Time - US)', offset: -6 },
  { value: 'CDT', label: 'CDT (Central Daylight Time - US)', offset: -5 },

  // US Mountain
  { value: 'MST', label: 'MST (Mountain Standard Time)', offset: -7 },
  { value: 'MDT', label: 'MDT (Mountain Daylight Time)', offset: -6 },

  // US Pacific
  { value: 'PST', label: 'PST (Pacific Standard Time)', offset: -8 },
  { value: 'PDT', label: 'PDT (Pacific Daylight Time)', offset: -7 },

  // Alaska
  { value: 'AKST', label: 'AKST (Alaska Standard Time)', offset: -9 },
  { value: 'AKDT', label: 'AKDT (Alaska Daylight Time)', offset: -8 },

  // Hawaii (no DST)
  { value: 'HST', label: 'HST (Hawaii Standard Time)', offset: -10 },

  // Europe
  { value: 'CET', label: 'CET (Central European Time)', offset: 1 },
  { value: 'CEST', label: 'CEST (Central European Summer Time)', offset: 2 },
  { value: 'EET', label: 'EET (Eastern European Time)', offset: 2 },
  { value: 'EEST', label: 'EEST (Eastern European Summer Time)', offset: 3 },

  // Asia
  { value: 'IST', label: 'IST (India Standard Time)', offset: 5.5 },
  { value: 'CST_CHINA', label: 'CST (China Standard Time)', offset: 8 },
  { value: 'JST', label: 'JST (Japan Standard Time)', offset: 9 },

  // Australia
  { value: 'AEST', label: 'AEST (Australian Eastern Standard Time)', offset: 10 },
  { value: 'AEDT', label: 'AEDT (Australian Eastern Daylight Time)', offset: 11 },

  // New Zealand
  { value: 'NZST', label: 'NZST (New Zealand Standard Time)', offset: 12 },
  { value: 'NZDT', label: 'NZDT (New Zealand Daylight Time)', offset: 13 },
];

export default function TimeZoneConverterClient() {
  const [fromDate, setFromDate] = useState('');
  const [fromTime, setFromTime] = useState('');
  const [fromZone, setFromZone] = useState('EST');
  const [toZone, setToZone] = useState('PST');
  const [result, setResult] = useState<{
    time: string;
    date: string;
    datetime: Date;
  } | null>(null);

  const convertTime = () => {
    if (!fromDate || !fromTime) return;

    // Parse the input date and time
    // Handle both 24-hour format (HH:MM) and 12-hour format (HH:MM AM/PM)
    let hour24, minute;

    if (fromTime.includes('AM') || fromTime.includes('PM')) {
      // 12-hour format
      const [time, period] = fromTime.trim().split(' ');
      const [h, m] = time.split(':');
      hour24 = parseInt(h);
      minute = parseInt(m);
      if (period === 'PM' && hour24 !== 12) hour24 += 12;
      if (period === 'AM' && hour24 === 12) hour24 = 0;
    } else {
      // 24-hour format
      const [hours, minutes] = fromTime.split(':');
      hour24 = parseInt(hours);
      minute = parseInt(minutes);
    }

    const fromZoneObj = timeZones.find(tz => tz.value === fromZone);
    const toZoneObj = timeZones.find(tz => tz.value === toZone);

    if (!fromZoneObj || !toZoneObj) return;

    // Simple approach: calculate the time difference in hours between zones
    const [year, month, day] = fromDate.split('-').map(Number);

    // Create a date object for the input date and time
    const inputDate = new Date(year, month - 1, day, hour24, minute);

    // Calculate the offset difference between the two time zones
    const offsetDifference = toZoneObj.offset - fromZoneObj.offset;

    // Apply the offset difference to get the target time
    const targetDate = new Date(inputDate.getTime() + (offsetDifference * 3600000));

    // Format the result
    const targetHours = targetDate.getHours();
    const targetMinutes = targetDate.getMinutes();
    const targetPeriod = targetHours >= 12 ? 'PM' : 'AM';
    const displayHours = targetHours > 12 ? targetHours - 12 : targetHours === 0 ? 12 : targetHours;

    setResult({
      time: `${String(displayHours).padStart(2, '0')}:${String(targetMinutes).padStart(2, '0')} ${targetPeriod}`,
      date: targetDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      datetime: targetDate
    });
  };

  const reset = () => {
    setFromDate('');
    setFromTime('');
    setResult(null);
  };

  const getCurrentDateTime = () => {
    const now = new Date();
    const date = now.toISOString().split('T')[0];
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return { date, time: `${hours}:${minutes}` };
  };

  useEffect(() => {
    const current = getCurrentDateTime();
    setFromDate(current.date);
    setFromTime(current.time);
  }, []);

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
          <Globe className="h-8 w-8 text-blue-600" />
        </div>
        <ToolPageIntro title="Time Zone Converter">
                Convert time between different time zones instantly with our free time zone converter. Support for major time zones including EST, PST, GMT, CET, IST, and more.
            </ToolPageIntro>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <label htmlFor="from-date" className="block text-sm font-medium text-gray-700 mb-1">
              From Date
            </label>
            <input
              type="date"
              id="from-date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label htmlFor="from-time" className="block text-sm font-medium text-gray-700 mb-1">
              From Time
            </label>
            <input
              type="time"
              id="from-time"
              value={fromTime}
              onChange={(e) => setFromTime(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label htmlFor="from-zone" className="block text-sm font-medium text-gray-700 mb-1">
              From Time Zone
            </label>
            <select
              id="from-zone"
              value={fromZone}
              onChange={(e) => setFromZone(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {timeZones.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="to-zone" className="block text-sm font-medium text-gray-700 mb-1">
              To Time Zone
            </label>
            <select
              id="to-zone"
              value={toZone}
              onChange={(e) => setToZone(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {timeZones.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={convertTime}
              disabled={!fromDate || !fromTime}
              className={`px-6 py-2 rounded-lg text-white font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${!fromDate || !fromTime
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
                }`}
            >
              Convert Time
            </button>
          </div>
        </div>

        <div className="flex justify-center space-x-3">
          <button
            onClick={reset}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2"
          >
            Reset
          </button>
        </div>
      </div>

      {result && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Conversion Result</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-600">{result.time}</div>
                <div className="text-sm text-gray-600">Converted Time</div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg text-center">
                <div className="text-lg font-semibold text-green-600">{result.date}</div>
                <div className="text-sm text-gray-600">Converted Date</div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-center text-gray-700">
                <ArrowRight className="h-5 w-5 mr-2" />
                <span className="text-sm">
                  <strong>Conversion:</strong> {fromZone} → {toZone}
                </span>
              </div>
              <div className="flex items-center justify-center text-gray-700 mt-2">
                <Clock className="h-5 w-5 mr-2" />
                <span className="text-sm">
                  <strong>Full DateTime:</strong> {result.datetime.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <InformationalContent
        title={timeZoneConverterContent.title}
        description={timeZoneConverterContent.description}
        content={timeZoneConverterContent.content}
        faqs={timeZoneConverterContent.faqs}
        relatedTools={timeZoneConverterContent.relatedTools}
      />
    </div>
  );
}
